import { getHTMLContentBetween } from '@tiptap-pro/extension-ai';
import type { Editor } from '@tiptap/core';
import { Extension } from '@tiptap/core';

const getOutputConstraints = (isInTableCell: boolean): string => {
  if (isInTableCell) {
    return `
    - Return only answer to the user prompt
    - Maximum 3 paragraphs
    - Maximum 500 words
    - Plain text only (no styling, no formatting)
    - Must be descriptive but concise
    - No HTML tags or markdown formatting`;
  }
  return `
  - Return only answer to the user prompt
  - If selected text is plain text, don't include any HTML tags or markdown formatting
  - Maximum 1 page
  - Maximum 1,000-1,200 words
  - Maximum 6 paragraphs
  - Maximum 4-5 sentences per paragraph
  `;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tiptapAi: {
      fixGrammar: () => ReturnType;
      simplifyText: () => ReturnType;
      completeSentence: () => ReturnType;
      generateWithPrompt: (prompt: string, overwriteBlock?: boolean) => ReturnType;
    };
  }
}

type TiptapAiStorage = {
  selectionStartBeforeAi: number | null;
  isTableCellEditor?: boolean;
  fontFamilyBeforeAi: string | null;
};

const generatePrompt = (prompt: string, isInTableCell: boolean, selectedText?: string) => {
  const promptTemplate = `
  User Prompt:
  ${prompt}

  Output Constraints:
  ${getOutputConstraints(isInTableCell)}
  `;

  if (selectedText) {
    return `${promptTemplate}
    Selected Text:
    ${selectedText}`;
  }

  return promptTemplate;
};

const getFontFamily = (editor: Editor): string | null => {
  const attributes = editor.getAttributes('textStyle');
  return attributes?.fontFamily || null;
};

export const TiptapAiExtension = Extension.create({
  name: 'tiptapAiExtension',

  addStorage(): TiptapAiStorage {
    return {
      selectionStartBeforeAi: null,
      isTableCellEditor: false,
      fontFamilyBeforeAi: null,
    };
  },

  addCommands() {
    return {
      fixGrammar: () => {
        return ({ editor }) => {
          const { from, to } = editor.state.selection;
          editor.storage.tiptapAiExtension.selectionStartBeforeAi = from;

          return editor.chain().aiFixSpellingAndGrammar({ format: 'rich-text', insertAt: { from, to } }).run();
        };
      },
      simplifyText: () => {
        return ({ editor }) => {
          const { from, to } = editor.state.selection;
          editor.storage.tiptapAiExtension.selectionStartBeforeAi = from;
          return editor.chain().aiSimplify({ format: 'rich-text', insertAt: { from, to } }).run();
        };
      },
      completeSentence: () => {
        return ({ editor }) => {
          const { from, to, empty } = editor.state.selection;
          let selectedText = '';
          if (!empty) {
            selectedText = getHTMLContentBetween(editor, from, to);
            editor.storage.tiptapAiExtension.selectionStartBeforeAi = from;
          } else {
            selectedText = editor.state.doc.textBetween(0, to, ' ');
            editor.storage.tiptapAiExtension.selectionStartBeforeAi = 0;
          }

          return editor
            .chain()
            .focus()
            .aiTextPrompt({
              text: `Complete the following sentence and return only the completed part of the sentence: ${selectedText}`,
              startsInline: true,
              insertAt: to,
            })
            .run();
        };
      },
      generateWithPrompt: (prompt: string, overwriteBlock = false) => {
        return ({ editor }) => {
          const storage = editor.storage.tiptapAiExtension as TiptapAiStorage;
          const { from, to, empty } = editor.state.selection;

          const isInTableCell = storage.isTableCellEditor === true;
          const format = isInTableCell ? 'plain-text' : 'rich-text';

          storage.fontFamilyBeforeAi = getFontFamily(editor);

          if (overwriteBlock) {
            storage.selectionStartBeforeAi = 0;

            const from = 0;
            const to = editor.state.doc.content.size;
            return editor
              .chain()
              .focus()
              .aiTextPrompt({
                text: generatePrompt(prompt, isInTableCell),
                format,
                insertAt: { from, to },
              })
              .run();
          } else {
            if (!empty) {
              const selectedText = getHTMLContentBetween(editor, from, to);
              storage.selectionStartBeforeAi = from;
              return editor
                .chain()
                .focus()
                .aiTextPrompt({
                  text: generatePrompt(prompt, isInTableCell, selectedText),
                  format,
                  insertAt: { from, to },
                })
                .run();
            } else {
              storage.selectionStartBeforeAi = to;
              return editor
                .chain()
                .focus()
                .aiTextPrompt({
                  text: generatePrompt(prompt, isInTableCell),
                  format,
                  insertAt: to,
                })
                .run();
            }
          }
        };
      },
    };
  },
  addKeyboardShortcuts() {
    return {
      'Shift-Tab': () => {
        return this.editor.commands.completeSentence();
      },
    };
  },
});
